import React, { ChangeEvent, useCallback, useState } from 'react';
import { AsYouType, parsePhoneNumber } from 'libphonenumber-js';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  Modal,
  Button,
  ModalContent,
  ModalOverlay,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  Box,
  Checkbox,
} from '@chakra-ui/react';

import { Span } from '../../components/Typography';
import PhoneInput from './PhoneInput';
import Card from '../../components/Card';
import useForm from '../../utils/useForm';
import { updateCustomer } from '../api';
import { useGlobalContext } from '../state/GlobalContext';
import {
  checkPhoneNumberRequirements,
  checkIsValidCountry,
} from '../../utils/phoneUtils';

const MarketingState = {
  SUBSCRIBED: 'SUBSCRIBED',
  NOT_SUBSCRIBED: 'NOT_SUBSCRIBED',
};

interface PhoneManagementProps {
  subscription;
  onUpdate: (
    msg: string,
    newValues: {
      Phone: string;
      AcceptsSMSMarketing: boolean;
    },
  ) => void;
  onError: (msg: string) => void;
  shopNotifyBySMS?: boolean;
}

const DUPLICATED_ERROR_MESSAGE = 'User errors';

const PhoneManagement = ({
  shopNotifyBySMS,
  subscription,
  onUpdate,
  onError,
}: PhoneManagementProps): React.ReactElement => {
  const { t } = useTranslation();
  const {
    Phone: phone = '',
    SMSMarketingConsent: {
      marketingState = MarketingState.NOT_SUBSCRIBED,
    } = {},
  } = subscription.Customer;

  const acceptsSMSMarketing = marketingState === MarketingState.SUBSCRIBED;

  const {
    isValid,
    isModalOpen,
    closeModal,
    loading,
    openModal,
    isValidCountry,
    values,
    handleChange,
    handleCheckboxChange,
    handleUpdateCustomer,
    handleRemovePhone,
  } = useCustomerFields({ phone, acceptsSMSMarketing, onUpdate, onError, t });

  const hasPhoneNumber = !!phone;
  const phoneIsValid = phone ? checkIsValidCountry(phone) : true;

  const modalTitle = hasPhoneNumber
    ? t('sms.edit_phone')
    : t('sms.enable_phone');

  return (
    <Card
      ariaLabel={t('aria_labels.edit_phone_number')}
      className="bundle-card--sms"
      headerText={t('sms.title')}
      btnText={hasPhoneNumber ? t('sms.edit') : t('sms.add')}
      onClick={openModal}
      testId="phone-card"
    >
      {hasPhoneNumber && (
        <Text marginBottom={phoneIsValid ? '0px' : '1em'}>
          {new AsYouType('US').input(phone)}
        </Text>
      )}
      {hasPhoneNumber && !phoneIsValid ? (
        <Text marginBottom="0px">{t('sms.invalid_country')}</Text>
      ) : null}
      <Modal isOpen={isModalOpen} onClose={closeModal} isCentered>
        <ModalOverlay className="awt-add-phone-number-modal--overlay">
          <ModalContent className="awt-add-phone-number-modal">
            <ModalHeader>{modalTitle}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box>
                <Text>
                  {shopNotifyBySMS
                    ? t('sms.enable_phone_notifications_text')
                    : t('sms.enable_phone_text')}
                </Text>
              </Box>
              <Box>
                <SMSUpdateForm>
                  <FormControl id="phone-block">
                    <PhoneInput
                      defaultCountry="US"
                      type="tel"
                      name="phone"
                      id={'phone'}
                      placeholder={t('sms.phone_placeholder')}
                      value={new AsYouType('US').input(values.phone)}
                      onChange={(value = '') =>
                        handleChange({ target: { name: 'phone', value } })
                      }
                      className={
                        !isValid || !isValidCountry
                          ? 'bundleapp-input-error'
                          : ''
                      }
                      required
                    />
                    <label htmlFor="phone">{t('sms.phone')}</label>
                    {!isValid ? (
                      <Span modifiers={['caption', 'destructive']}>
                        {t('sms.invalid_phone')}
                      </Span>
                    ) : null}
                    {!isValidCountry ? (
                      <Span modifiers={['caption', 'destructive']}>
                        {t('sms.invalid_country')}
                      </Span>
                    ) : null}
                  </FormControl>
                  <FormControl>
                    <Checkbox
                      id="acceptsSMSMarketing"
                      name="acceptsSMSMarketing"
                      size="lg"
                      isChecked={values.acceptsSMSMarketing}
                      onChange={handleCheckboxChange}
                    >
                      {t('sms.consent_marketing_label')}
                    </Checkbox>
                  </FormControl>
                  {phone ? (
                    <Button
                      width="full"
                      onClick={handleRemovePhone}
                      marginTop="10px"
                    >
                      {t('sms.remove_phone')}
                    </Button>
                  ) : null}
                </SMSUpdateForm>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button onClick={closeModal} variant="outlined" mr="4">
                {t('titles.cancel')}
              </Button>
              <Button
                isLoading={loading}
                onClick={handleUpdateCustomer}
                isDisabled={!values.phone || !isValid || !isValidCountry}
              >
                {t('titles.continue')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </Card>
  );
};

interface CustomerFieldsProps {
  phone: string;
  acceptsSMSMarketing: boolean;
  onUpdate: (
    msg: string,
    newValues: {
      Phone: string;
      AcceptsSMSMarketing: boolean;
    },
  ) => void;
  onError: (msg: string) => void;
  t: (key: string) => string;
}

const useCustomerFields = ({
  phone,
  acceptsSMSMarketing,
  onUpdate,
  onError,
  t,
}: CustomerFieldsProps) => {
  const { customerId, authToken, subscriptionId } = useGlobalContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(phone ? false : true);
  const [isValidCountry, setIsValidCountry] = useState(phone ? false : true);
  const { values, handleChange } = useForm<{
    phone: string;
    acceptsSMSMarketing: boolean;
  }>({
    initialValues: {
      phone,
      acceptsSMSMarketing,
    },
  });

  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const openModal = useCallback(() => setIsModalOpen(true), []);

  React.useEffect(() => {
    const isPhoneValid = values.phone
      ? checkPhoneNumberRequirements(values.phone)
      : true;
    setIsValid(isPhoneValid);
    if (isPhoneValid && values.phone) {
      setIsValidCountry(checkIsValidCountry(values.phone));
    } else {
      setIsValidCountry(true);
    }
  }, [values.acceptsSMSMarketing, values.phone]);

  const handleUpdateCustomer = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        setLoading(true);
        await updateCustomer(customerId, authToken, subscriptionId, {
          phone: parsePhoneNumber(values.phone).number || values.phone,
          acceptsSMSMarketing: values.acceptsSMSMarketing,
        });
        onUpdate(t('subscription.customer_phone_added_success'), {
          Phone: values.phone,
          AcceptsSMSMarketing: values.acceptsSMSMarketing,
        });
      } catch (err: any) {
        if (
          err.statusCode === 400 &&
          err.message === DUPLICATED_ERROR_MESSAGE
        ) {
          onError(t('subscription.customer_update_error'));
        } else {
          onError(t('sms.duplicated_phone_error'));
        }
      } finally {
        setLoading(false);
        closeModal();
      }
    },
    [
      authToken,
      closeModal,
      customerId,
      onError,
      onUpdate,
      subscriptionId,
      t,
      values.acceptsSMSMarketing,
      values.phone,
    ],
  );

  const handleCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.checked;
      handleChange({
        target: { name: 'acceptsSMSMarketing', value },
      });
    },
    [handleChange],
  );

  const handleRemovePhone = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        setLoading(true);
        await updateCustomer(customerId, authToken, subscriptionId, {
          phone: '',
          acceptsSMSMarketing: false,
        });
        handleChange({ target: { name: 'phone', value: '' } });
        onUpdate(t('subscription.customer_phone_removed_success'), {
          Phone: '',
          AcceptsSMSMarketing: false,
        });
      } catch (err: any) {
        onError(t('subscription.customer_update_error'));
      } finally {
        setLoading(false);
        closeModal();
      }
    },
    [
      authToken,
      closeModal,
      customerId,
      handleChange,
      onError,
      onUpdate,
      subscriptionId,
      t,
    ],
  );

  return {
    isModalOpen,
    closeModal,
    openModal,
    handleCheckboxChange,
    handleUpdateCustomer,
    handleRemovePhone,
    loading,
    isValid,
    isValidCountry,
    handleChange,
    values,
  };
};

export default PhoneManagement;

const SMSUpdateForm = styled.form`
  margin: 3rem auto 0;
  width: 100%;
  display: flex;
  gap: 2.5rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .chakra-form-control {
    .chakra-checkbox {
      position: relative;
      inset: initial;
    }
  }
  .bundleapp-flex {
    margin-bottom: 5px;
  }

  select {
    padding: 8px 20px;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
  }

  input {
    outline: none;
    border: none;
  }

  .bundleapp-fieldwrap {
    position: relative;
  }

  .bundleapp-input-error {
    border: 1px solid #ff002e;
  }

  label {
    padding: 0px 8px;
    border-radius: 8px;
    background: #fff;
    font-size: 12px;
    left: 18px;
    position: absolute;
    text-transform: capitalize;
    top: -13px;
    width: auto;
  }
`;
